import { api } from './api'
import { SHORT_ROUTE_THRESHOLD_KM } from './routingConfig'

function haversineKm(a: [number, number], b: [number, number]): number {
  const toRad = (v: number) => (v * Math.PI) / 180
  const R = 6371
  const dLat = toRad(b[0] - a[0])
  const dLng = toRad(b[1] - a[1])
  const lat1 = toRad(a[0])
  const lat2 = toRad(b[0])
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
}

function approxRouteKm(points: [number, number][]): number {
  let km = 0
  for (let i = 0; i < points.length - 1; i++) km += haversineKm(points[i], points[i + 1])
  return km
}

// Returns true if the coordinate is plausibly inside Russia (rough bounding boxes).
// Kaliningrad exclave handled separately.
function isLikelyRussiaCoordinate([lat, lng]: [number, number]): boolean {
  if (lat < 41 || lat > 82) return false
  // Kaliningrad exclave: ~54–56°N, 19–23°E
  if (lng >= 19 && lng <= 23 && lat >= 54 && lat <= 56) return true
  // Main territory: roughly 26–169°E
  return lng >= 26 && lng <= 169
}

function areAllPointsLikelyInRussia(points: [number, number][]): boolean {
  return points.every(isLikelyRussiaCoordinate)
}

export type RouteClass = 'domestic_short' | 'domestic_long' | 'cross_border' | 'unknown'

export type RouteClassification = {
  kind: RouteClass
  approxKm: number
}

export function classifyRoute(points: [number, number][]): RouteClassification {
  if (points.length < 2) return { kind: 'unknown', approxKm: 0 }
  const approxKm = approxRouteKm(points)
  const inRussia = areAllPointsLikelyInRussia(points)
  if (!inRussia) return { kind: 'cross_border', approxKm }
  if (approxKm < SHORT_ROUTE_THRESHOLD_KM) return { kind: 'domestic_short', approxKm }
  return { kind: 'domestic_long', approxKm }
}

export type RouteProvider = 'osrm' | 'ors' | 'straight'

export type ResolvedRoute = {
  polyline: [number, number][]
  provider: RouteProvider
}

/**
 * leavesRussiaFast() is a SECONDARY post-build guard only.
 * It does NOT participate in primary provider selection.
 * Used solely as a safety check on OSRM result for domestic_short routes.
 */
function leavesRussiaFast(polyline: [number, number][]): boolean {
  for (const [lat, lng] of polyline) {
    if (lng > 45 && lng < 90 && lat < 50) return true
    if (lng > 73 && lng < 135 && lat < 48) return true
    if (lng > 125 && lat < 42.5) return true
  }
  return false
}

/**
 * domesticOrsRoute asks ORS for a route kept on Russian roads and, when that finds nothing, for the
 * same route without the constraints. The "in Russia" check is a rough bounding box that also covers
 * Kazakhstan and Belarus, so a trip to Almaty or Minsk lands here — and ORS cannot reach a point in a
 * country it was told to avoid. The retry draws the real cross-border road instead of a straight line.
 */
async function domesticOrsRoute(points: [number, number][]): Promise<[number, number][]> {
  const ors = await api.orsRoute(points)
  if (ors.length > 0) return ors
  return api.orsRoute(points, { skipRussiaConstraints: true })
}

/**
 * Proactive routing strategy: classify the route by distance AND geography,
 * then select the provider by policy.
 *
 * domestic_short (<1200km, all points in Russia): ORS (Russian constraints, then none) → OSRM (demo) fallback → straight
 * domestic_long  (≥1200km, all points in Russia): ORS (Russian constraints, then none) → straight
 * cross_border   (any point outside Russia):      ORS without Russian constraints → straight
 * unknown        (<2 points):                     straight
 */
export async function resolveRoute(points: [number, number][]): Promise<ResolvedRoute> {
  const cls = classifyRoute(points)

  if (cls.kind === 'unknown') {
    return { polyline: points, provider: 'straight' }
  }

  if (cls.kind === 'domestic_short') {
    // ORS is the primary provider everywhere. The public OSRM demo server is only
    // a fallback for short routes when ORS is unavailable (no key, quota, error),
    // so we don't depend on a no-SLA host. The leavesRussiaFast guard still applies
    // to OSRM results.
    const ors = await domesticOrsRoute(points)
    if (ors.length > 0) {
      console.debug('[routing] provider: ors (domestic short), km:', Math.round(cls.approxKm))
      return { polyline: ors, provider: 'ors' }
    }
    const osrm = await api.osrmRoute(points)
    if (osrm.length > 0 && !leavesRussiaFast(osrm)) {
      console.debug('[routing] provider: osrm (ors unavailable), km:', Math.round(cls.approxKm))
      return { polyline: osrm, provider: 'osrm' }
    }
    if (osrm.length > 0) {
      console.debug('[routing] provider: osrm (ors unavailable, left-Russia tolerated), km:', Math.round(cls.approxKm))
      return { polyline: osrm, provider: 'osrm' }
    }
    console.debug('[routing] provider: straight (all failed), km:', Math.round(cls.approxKm))
    return { polyline: points, provider: 'straight' }
  }

  if (cls.kind === 'domestic_long') {
    const ors = await domesticOrsRoute(points)
    if (ors.length > 0) {
      console.debug('[routing] provider: ors (domestic long), km:', Math.round(cls.approxKm))
      return { polyline: ors, provider: 'ors' }
    }
    console.debug('[routing] provider: straight (ors failed, domestic long), km:', Math.round(cls.approxKm))
    return { polyline: points, provider: 'straight' }
  }

  // cross_border — skip Russian constraints (no avoid_countries, no corridor waypoints)
  const ors = await api.orsRoute(points, { skipRussiaConstraints: true })
  if (ors.length > 0) {
    console.debug('[routing] provider: ors (cross_border), km:', Math.round(cls.approxKm))
    return { polyline: ors, provider: 'ors' }
  }
  console.debug('[routing] provider: straight (cross_border, ors failed), km:', Math.round(cls.approxKm))
  return { polyline: points, provider: 'straight' }
}
